import { useState, useCallback, useEffect } from 'react';
import { classifyInvitedUserAdvertisersFromApi } from '../utils/classifyAdvertiserAccess';

export const useCurrentUserData = (currentUserData) => {
  const [currentUserAdvertisers, setCurrentUserAdvertisers] = useState({
    advertisers_utc: [],
    advertisers_non_utc: [],
    advertisers_ctv: [],
    advertisers_gtv: []
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch current user's advertisers
  const fetchCurrentUserAdvertisers = useCallback(async () => {
    if (!currentUserData?.email_id) return;

    setIsLoading(true);
    setError(null);

    try {
      console.log('🔍 Fetching current user advertisers for:', currentUserData.email_id);

      if (currentUserData.role === 'admin') {
        // Admin: Get all advertisers from get_advertiser_meta_details
        const response = await fetch('/get_advertiser_meta_details', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.data && Array.isArray(data.data)) {
          // Process all advertisers for admin
          const jstAdvertisers = data.data.filter((item) => item.timezone === 'JST');
          const utcAdvertisers = data.data.filter((item) => item.timezone !== 'JST' && item.timezone !== '');
          const emptyTimezoneAdvertisers = data.data.filter((item) => item.timezone === '');

          const allUtcAdvertisers = [...utcAdvertisers, ...emptyTimezoneAdvertisers];

          const processedData = {
            advertisers_utc: allUtcAdvertisers.map((item) => ({
              id: item.advertiser_guid,
              name: item.advertiser_name,
              org_name: item.org_name,
              timezone: item.timezone || 'UTC',
              sfId: item.salesforce_account_id || ''
            })),
            advertisers_non_utc: jstAdvertisers.map((item) => ({
              id: item.advertiser_guid,
              name: item.advertiser_name,
              org_name: item.org_name,
              timezone: item.timezone,
              sfId: item.salesforce_account_id || ''
            })),
            advertisers_ctv: [],
            advertisers_gtv: []
          };

          setCurrentUserAdvertisers(processedData);
        }
      } else {
        // Internal: Get only their accessible advertisers
        const response = await fetch('/get_user_advertiser_details', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: currentUserData.email_id })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data?.data && Array.isArray(data.data)) {
          // Process current user's advertisers
          let selectedData = data.data.filter((item) => item.selected === 'true');

          // Fallback: if all have selected=false, treat as selected
          if (selectedData.length === 0 && data.data.every((item) => item.selected === 'false')) {
            selectedData = data.data;
          }

          // Fallback: if still no data, use all
          if (selectedData.length === 0) {
            selectedData = data.data;
          }

          const uniqueSelectedData = [];
          const seenGuids = new Set();
          for (const item of selectedData) {
            if (!seenGuids.has(item.advertiser_guid)) {
              seenGuids.add(item.advertiser_guid);
              uniqueSelectedData.push(item);
            }
          }

          const classified = classifyInvitedUserAdvertisersFromApi(uniqueSelectedData);

          const processedData = {
            advertisers_utc: classified.advertisers_utc,
            advertisers_non_utc: classified.advertisers_non_utc,
            advertisers_ctv: classified.advertisers_ctv,
            advertisers_gtv: classified.advertisers_gtv
          };

          setCurrentUserAdvertisers(processedData);
          console.log('🔍 Internal - Current user advertisers processed:', processedData);
        } else {
          throw new Error('Invalid API response format for current user');
        }
      }
    } catch (err) {
      console.error('🔍 Error fetching current user advertisers:', err);
      setError(err.message);

      // Set empty data on error
      setCurrentUserAdvertisers({
        advertisers_utc: [],
        advertisers_non_utc: [],
        advertisers_ctv: [],
        advertisers_gtv: []
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentUserData?.email_id, currentUserData?.role]);

  // Fetch data when component mounts or currentUserData changes
  useEffect(() => {
    if (currentUserData?.email_id && currentUserData?.role) {
      fetchCurrentUserAdvertisers();
    }
  }, [currentUserData?.email_id, currentUserData?.role, fetchCurrentUserAdvertisers]);

  // Get all current user advertisers as a flat list
  const getAllCurrentUserAdvertisers = useCallback(() => {
    return [
      ...(currentUserAdvertisers.advertisers_utc || []),
      ...(currentUserAdvertisers.advertisers_non_utc || []),
      ...(currentUserAdvertisers.advertisers_ctv || []),
      ...(currentUserAdvertisers.advertisers_gtv || [])
    ];
  }, [currentUserAdvertisers]);

  // Check if current user has access to specific advertiser
  const hasAccessToAdvertiser = useCallback(
    (advertiserName) => {
      const allAdvertisers = getAllCurrentUserAdvertisers();
      return allAdvertisers.some((adv) => adv.name === advertiserName || adv.advertiser_name === advertiserName);
    },
    [getAllCurrentUserAdvertisers]
  );

  return {
    currentUserAdvertisers,
    isLoading,
    error,
    fetchCurrentUserAdvertisers,
    getAllCurrentUserAdvertisers,
    hasAccessToAdvertiser
  };
};
